import React, { useId } from 'react';
import './FormField.css';

/**
 * FormField — accessible label/input/error/help primitive.
 *
 * Wires:
 *   - <label htmlFor={inputId}> tied to the input
 *   - aria-invalid when there's an error
 *   - aria-required when required
 *   - aria-describedby pointing at error + help when present
 *   - error message rendered inside an aria-live="polite" region so AT
 *     announces it without taking focus
 *
 * `as` selects between input / textarea / select. For native <select> pass
 * the <option> children. Anything more complex (combobox, custom select)
 * should accept and forward `id`/`aria-describedby` and use renderInput.
 */
const FormField = ({
  id,
  name,
  label,
  type = 'text',
  value,
  onChange,
  onBlur,
  required = false,
  error,
  help,
  placeholder,
  autoComplete,
  inputMode,
  className = '',
  inputClassName = '',
  as = 'input',
  rows,
  children,
  renderInput,
  ...rest
}) => {
  const reactId = useId();
  const inputId = id || `ff-${reactId}`;
  const errorId = `${inputId}-error`;
  const helpId = `${inputId}-help`;

  const describedBy = [error ? errorId : null, help ? helpId : null]
    .filter(Boolean)
    .join(' ') || undefined;

  const sharedProps = {
    id: inputId,
    name: name || inputId,
    value,
    onChange,
    onBlur,
    placeholder,
    autoComplete,
    inputMode,
    required,
    'aria-invalid': error ? true : undefined,
    'aria-required': required ? true : undefined,
    'aria-describedby': describedBy,
    className: `form-field__control ${error ? 'is-invalid' : ''} ${inputClassName}`.trim(),
    ...rest,
  };

  let control;
  if (renderInput) {
    control = renderInput(sharedProps);
  } else if (as === 'textarea') {
    control = <textarea rows={rows || 4} {...sharedProps} />;
  } else if (as === 'select') {
    control = <select {...sharedProps}>{children}</select>;
  } else {
    control = <input type={type} {...sharedProps} />;
  }

  return (
    <div className={`form-field ${className}`}>
      <label htmlFor={inputId} className="form-field__label">
        {label}
        {required && (
          <span className="form-field__required" aria-hidden="true">
            {' *'}
          </span>
        )}
      </label>
      {control}
      {help && (
        <p id={helpId} className="form-field__help">
          {help}
        </p>
      )}
      <p
        id={errorId}
        className="form-field__error"
        role={error ? 'alert' : undefined}
        aria-live="polite"
      >
        {error || ''}
      </p>
    </div>
  );
};

export default FormField;
